<script setup lang="ts">
   import { onMounted, onUnmounted, ref, watch } from 'vue';
   import 'mmenu-light/dist/mmenu-light.css';
   import PublicNavListItem from '~/components/PublicNavListItem.vue';
   import NuxtTypedLink from '~/components/NuxtTypedLink.vue';
   import { usePdpNavigation } from '~/composables/usePdpNavigation';

   const route = useRoute();
   const { visibleItems } = usePdpNavigation();

   const menuElementRef = ref(null);
   let drawerInstance: any = null;

   onMounted(async () => {
      if (!menuElementRef.value) {
         return;
      }

      // mmenu-light touches browser globals at import time — load it client-side only.
      const { default: MmenuLight } = await import('mmenu-light');
      const menu = new MmenuLight(menuElementRef.value, '(max-width: 1023px)');

      menu.navigation({});
      drawerInstance = menu.offcanvas({});
   });

   onUnmounted(() => {
      // Close and cleanup the drawer when component unmounts
      if (drawerInstance) {
         drawerInstance.close();
      }
   });

   function openDrawer() {
      if (drawerInstance) {
         drawerInstance.open();
      }
   }

   defineExpose({
      openMenu: openDrawer,
   });

   watch(
      () => route.fullPath,
      () => {
         if (drawerInstance) {
            drawerInstance.close();
         }
      },
   );
</script>

<template>
   <nav ref="menuElementRef" class="mx-auto" id="pdp-main-menu">
      <ul class="flex">
         <template v-for="item in visibleItems" :key="item.label">
            <li
               v-if="item.items && item.items.length > 0"
               class="group relative flex">
               <span
                  class="flex cursor-pointer items-center justify-between gap-1 whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                  <i v-if="item.icon" :class="`pi ${item.icon} mr-1`" style="font-size: 0.875rem !important;"></i>
                  {{ item.label }}
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     fill="currentColor"
                     class="hidden size-4 lg:block">
                     <path
                        fill-rule="evenodd"
                        d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z"
                        clip-rule="evenodd" />
                  </svg>
               </span>
               <ul
                  class="lg:pointer-events-none lg:absolute lg:left-0 lg:top-full lg:z-50 lg:min-w-48 lg:border lg:border-gray-200 lg:bg-white lg:opacity-0 lg:shadow-lg lg:transition-all lg:duration-200 lg:group-hover:pointer-events-auto lg:group-hover:opacity-100">
                  <template v-for="subItem in item.items" :key="subItem.label">
                     <li v-if="subItem.to" class="flex">
                        <NuxtTypedLink
                           :to="subItem.to"
                           class="flex w-full items-center justify-between whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                           {{ subItem.label }}
                        </NuxtTypedLink>
                     </li>
                     <li v-else class="flex">
                        <button
                           class="flex w-full items-center justify-between whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-left text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                           {{ subItem.label }}
                        </button>
                     </li>
                  </template>
               </ul>
            </li>
            <PublicNavListItem v-else-if="item.to" :to="item.to">
               <i v-if="item.icon" :class="`pi ${item.icon} mr-1`" style="font-size: 0.875rem !important;"></i>
               {{ item.label }}
            </PublicNavListItem>
         </template>
      </ul>
   </nav>
</template>

<style scoped></style>
